import { FC, useEffect } from 'react'
import { ColumnLayout } from '@cloudscape-design/components'
import FormInput from '../../../components/ControlledFormInputs/Input'
import { useForm } from 'react-hook-form'
import FormSelect from '../../../components/ControlledFormInputs/Select'
import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import CONSTANT from '../../../constants'
import {
  COST_CENTER_PATTERN,
  WBS_CODE_PATTERN
} from '../../../helper/fundingCodeValidation'
import { useNewPlatformFormStore } from '../../../stores/platforms/newPlatformFormStore'

const { COST_TYPE_LIST, DATA_CLASSIFICATION_LIST } = CONSTANT

type GcpPlatformTagsNewProps = {
  registerValidation?: (validate: () => Promise<boolean>) => void
  isInheritTagsChecked?: boolean
  projectLabel?: string
}

const getSelectedOptionLabel = (value: unknown) => {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'object' && value !== null) {
    const option = value as { label?: string; value?: string }
    return option.label || option.value || ''
  }

  return ''
}

const getGroupFromGcpProjectLabel = (label: string) => {
  const parts = label.trim().toLowerCase().split('-').filter(Boolean)

  if (parts[0] !== 'glo') {
    return ''
  }

  return parts[1] || ''
}

const GcpPlatformTagsNew: FC<GcpPlatformTagsNewProps> = ({
  registerValidation,
  isInheritTagsChecked,
  projectLabel
}) => {

  const {
    formValues: clusterFormValues
  } = useNewPlatformFormStore()

  const {
    formValues,
    actions: { setFormValues, setFormValidity }
  } = useGenericTagsManagementFormStore()

  const {
    control,
    formState: { isValid },
    setValue,
    trigger
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formValues,
    shouldUseNativeValidation: true
  })

  const selectedGcpProjectLabel =
    projectLabel || getSelectedOptionLabel(clusterFormValues.gcpProjectID)
  const derivedGroup = getGroupFromGcpProjectLabel(selectedGcpProjectLabel)

  useEffect(() => {
    setFormValidity(isValid)
  }, [isValid, setFormValidity])

  useEffect(() => {
    registerValidation?.(trigger)
  }, [registerValidation, trigger])

  useEffect(() => {
    if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'opex') {
      setValue('WBSCode', '')
      setFormValues('WBSCode', '')
    } else if (formValues?.fundingSourceType && formValues.fundingSourceType?.value === 'capex') {
      setValue('costCenter', '')
      setFormValues('costCenter', '')
    }
  }, [formValues.fundingSourceType, setValue, setFormValues])

  useEffect(() => {
    setValue('projectName', formValues.projectName)
    setValue('fundingSourceType', formValues.fundingSourceType)
    setValue('costCenter', formValues.costCenter)
    setValue('WBSCode', formValues.WBSCode)
    setValue('platformOwner', formValues.platformOwner)
    setValue('systemAlias', formValues.systemAlias)
    setValue('dataClassification', formValues.dataClassification)
  }, [formValues, setValue])

  useEffect(() => {
    setValue('group', derivedGroup, {
      shouldDirty: true,
      shouldValidate: true
    })
    setFormValues('group', derivedGroup)
  }, [derivedGroup, setValue, setFormValues])


  return (
    <ColumnLayout columns={2} variant="default">
      {/* <FormInput
        name="platformOwner"
        label="Platform Owner"
        type="text"
        placeholder="Enter Platform Owner"
        control={control}
        setFormValues={setFormValues}
      /> */}
      <FormInput
        label="Globe Group"
        name="group"
        type="text"
        control={control}
        rules={{
          required: 'Unable to derive group from selected GCP Project'
        }}
        setFormValues={setFormValues}
        placeholder="Derived from selected GCP Project"
        disabled
      />
      <FormSelect
        label="Data Classification"
        name="dataClassification"
        control={control}
        rules={{
          required: 'Please Select Option'
        }}
        setFormValues={setFormValues}
        options={DATA_CLASSIFICATION_LIST}
        placeholder="Select Data Classification"
        disabled={isInheritTagsChecked}
      />
      <FormSelect
        label="Funding Source"
        name="fundingSourceType"
        control={control}
        disabled={isInheritTagsChecked}
        rules={{
          required: 'Please Select Option'
        }}
        setFormValues={setFormValues}
        options={COST_TYPE_LIST}
        placeholder="Select Funding Source Type"
      />
      <br />
      <FormInput
        name="WBSCode"
        label="WBS Code"
        type="text"
        control={control}
        rules={{
          pattern: {
            value: WBS_CODE_PATTERN,
            message: 'Enter in all caps with at least three parts separated by dashes, e.g., PSD-OMN-21 or C-D-PR02-JE67CT-89-HW-01-GL-BE.'
          },
          minLength: {
            value: 3,
            message: 'Minimum length should be 3'
          }
        }}
        setFormValues={setFormValues}
        placeholder="Example: ISG-PROJECTA-23, ISG-PROJECTB-24, EDO-PROJECTC-24-SW-01"
        disabled={
          isInheritTagsChecked ||
          formValues?.fundingSourceType?.value === 'opex'
        }
      />
      <FormInput
        name="costCenter"
        label="Cost Center"
        type="text"
        control={control}
        rules={{
          pattern: {
            value: COST_CENTER_PATTERN,
            message: 'Please Enter all in CAPS, Ex. OP20-G,ISG24-G,PSD-OMN-21'
          },
          minLength: {
            value: 3,
            message: 'Minimum length should be 3'
          }
        }}
        setFormValues={setFormValues}
        placeholder="Example: ISG17-A, OP20-G, MP-CIV-20, HTT7-B,ISG01,PSD-TECCF/19"
        disabled={
          isInheritTagsChecked ||
          formValues?.fundingSourceType?.value === 'capex'
        }
      />
    </ColumnLayout>
  )
}

export default GcpPlatformTagsNew
